import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { ArrowDownIcon, ArrowUpIcon, SearchIcon, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Spinner } from '@air/primitive-spinner';

import { SEARCH_WIDTH } from '~/components/AssetModalV2/constants';
import {
  PDFSearchInput,
  type PDFSearchInputHandle,
} from '~/components/AssetModalV2/Visualizer/PDFVisualizer/PDFSearch/components/PDFSearchInput';
import { usePDFSearchHotkeys } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/PDFSearch/hooks/usePDFSearchHotkeys';

export type PDFSearchHandle = {
  setInitialValue: (value: string) => void;
  isOpen: () => boolean;
  openSearch: () => void;
};

export type PDFSearchProps = {
  currentResult: number;
  totalResults: number;
  isSearching: boolean;
  initialSearchPhrase?: string | null;
  onSearchChange: (value: string) => void;
  clearSearch: () => void;
  setCurrentResultIndex: (index: number) => void;
};

export const PDFSearch = memo(
  forwardRef<PDFSearchHandle, PDFSearchProps>(
    (
      {
        currentResult,
        totalResults,
        initialSearchPhrase,
        onSearchChange,
        isSearching,
        clearSearch,
        setCurrentResultIndex,
      }: PDFSearchProps,
      forwardedRef,
    ) => {
      const inputRef = useRef<PDFSearchInputHandle>(null);
      const [isExpanded, setIsExpanded] = useState(false);
      const pendingFocusRef = useRef<{ selectText: boolean } | null>(null);

      const openSearch = useCallback((selectText: boolean) => {
        // biome-ignore lint/suspicious/noUnnecessaryConditions: the ref is populated by React when the input below mounts; Biome only sees the initial `null`.
        if (inputRef.current) {
          // Already expanded — the input is mounted, so focus it directly.
          inputRef.current.focus(selectText);
          return;
        }

        // Collapsed — the input mounts only after the state update, so defer
        // the focus call to the layout effect below.
        pendingFocusRef.current = { selectText };
        setIsExpanded(true);
      }, []);

      useIsomorphicLayoutEffect(() => {
        if (!isExpanded) {
          return;
        }

        // biome-ignore lint/suspicious/noUnnecessaryConditions: `.current` is written by openSearch above; Biome only sees the initial `null`.
        if (pendingFocusRef.current) {
          inputRef.current?.focus(pendingFocusRef.current.selectText);
          pendingFocusRef.current = null;
        }
      }, [isExpanded]);

      useEffect(() => {
        if (initialSearchPhrase) {
          setIsExpanded(true);
        }
      }, [initialSearchPhrase]);

      const closeSearch = useCallback(() => {
        pendingFocusRef.current = null;
        setIsExpanded(false);
        inputRef.current?.clear();
        clearSearch();
      }, [clearSearch]);

      useImperativeHandle(forwardedRef, () => ({
        setInitialValue: (value) => {
          setIsExpanded(true);
          onSearchChange(value);
          requestAnimationFrame(() => inputRef.current?.setInitialValue(value));
        },
        isOpen: () => isExpanded,
        openSearch: () => openSearch(false),
      }));

      const onShowNextResult = useCallback(() => {
        if (currentResult < totalResults - 1) {
          setCurrentResultIndex(currentResult + 1);
        } else {
          setCurrentResultIndex(0);
        }
      }, [currentResult, setCurrentResultIndex, totalResults]);

      const onShowPreviousResult = useCallback(() => {
        if (currentResult > 0) {
          setCurrentResultIndex(currentResult - 1);
        } else {
          setCurrentResultIndex(totalResults - 1);
        }
      }, [currentResult, setCurrentResultIndex, totalResults]);

      usePDFSearchHotkeys({
        openSearch: () => openSearch(true),
        closeSearch,
        isExpanded,
        showNextResult: onShowNextResult,
        showPreviousResult: onShowPreviousResult,
        inputRef,
      });

      const handleOpenSearch = useCallback(() => {
        openSearch(true);
      }, [openSearch]);

      return (
        <>
          {!isExpanded ? (
            <IconButton
              adsIcon
              data-testid="PDF_SEARCH_BUTTON"
              onClick={handleOpenSearch}
              icon={SearchIcon}
              label="Search document"
              appearance="ghost"
              color="gray"
              size="medium"
            />
          ) : (
            <div
              className="box-content overflow-hidden rounded-8 border ads:border-input border-gray-5 ads:bg-input bg-gray-1 transition-all duration-300 ease-in-out"
              style={{ width: `${SEARCH_WIDTH}px` }}
            >
              <div className="flex size-full items-center rounded-8 pr-2">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-8">
                  {isSearching ? (
                    <Spinner className="size-6 ads:text-icon-subtle text-gray-9" />
                  ) : (
                    <SearchIcon size="lg" className="ads:text-icon-subtle text-gray-9" />
                  )}
                </span>
                <PDFSearchInput initialValue={initialSearchPhrase} onSearchChange={onSearchChange} ref={inputRef} />
                <div className="flex shrink-0 items-center gap-1">
                  {!!totalResults && (
                    <>
                      <p className="ads:text-metadata text-12 text-gray-9" data-testid="PDF_SEARCH_RESULTS_TOTAL">
                        {currentResult + 1} of {totalResults.toLocaleString()}
                      </p>
                      <IconButton
                        adsIcon
                        disabled={totalResults === 1}
                        label="Previous search result"
                        icon={ArrowUpIcon}
                        appearance="ghost"
                        color="gray"
                        size="small"
                        onClick={onShowPreviousResult}
                        data-testid="PDF_SEARCH_PREVIOUS_BUTTON"
                      />
                      <IconButton
                        adsIcon
                        disabled={totalResults === 1}
                        onClick={onShowNextResult}
                        label="Next search result"
                        icon={ArrowDownIcon}
                        appearance="ghost"
                        color="gray"
                        size="small"
                        data-testid="PDF_SEARCH_NEXT_BUTTON"
                      />
                    </>
                  )}
                  <IconButton
                    adsIcon
                    onClick={closeSearch}
                    label="Close document search"
                    icon={XIcon}
                    appearance="ghost"
                    color="gray"
                    size="small"
                    data-testid="PDF_SEARCH_CLOSE_BUTTON"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      );
    },
  ),
);

PDFSearch.displayName = 'PDFSearch';
